import { FC, ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuthStore } from "service";
import useRole, { UseRole } from "../../hooks/use-role";
import { AdRoutes } from "../../constants/ad-routes";

export interface RouteGuardProps {
  children: ReactNode;
  /**
   * Role predicate for this route. Omit to require only authentication.
   * e.g. <RouteGuard allow={({ isSuperAdmin }) => isSuperAdmin}>
   */
  allow?: (role: UseRole) => boolean;
  /**
   * Rendered instead of redirecting when `allow` rejects the current role.
   * Omit to redirect to the dashboard home, matching the router's existing
   * catch-all behavior for unmatched routes (`path="*"` -> AdRoutes.customer).
   */
  fallback?: ReactNode;
}

const RouteGuard: FC<RouteGuardProps> = (props) => {
  const { children, allow, fallback } = props;
  const { isLoggedIn } = useAuthStore();
  const role = useRole();

  // Defensive backstop, not the primary gate: app.tsx already swaps the whole
  // router out for AuthRouter when isLoggedIn flips to false, so in practice
  // this tree unmounts before this branch would ever be visible - and there
  // is no login route inside this router to redirect to anyway. Kept here in
  // case that top-level gating logic ever changes.
  if (!isLoggedIn) {
    return null;
  }

  if (allow && !allow(role)) {
    return fallback !== undefined ? (
      <>{fallback}</>
    ) : (
      <Navigate to={AdRoutes.customer} replace />
    );
  }

  return <>{children}</>;
};

export default RouteGuard;
